import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";
import WhatsAppButton from "@/components/WhatsAppButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { id: "all", label: "All Work" },
  { id: "wedding", label: "Wedding" },
  { id: "pre-wedding", label: "Pre-Wedding" },
  { id: "portrait", label: "Portrait" },
  { id: "commercial", label: "Commercial" },
  { id: "event", label: "Events" },
  { id: "videography", label: "Videography" },
];

export default function Portfolio() {
  const [portfolio, setPortfolio] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        const response = await axios.get(`${API}/portfolio`);
        setPortfolio(response.data);
      } catch (error) {
        console.error("Error fetching portfolio:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPortfolio();
  }, []);

  const filteredPortfolio = activeCategory === "all"
    ? portfolio
    : portfolio.filter((item) => item.category === activeCategory);

  const openLightbox = (index) => {
    setCurrentImage(index);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = "auto";
  };

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % filteredPortfolio.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + filteredPortfolio.length) % filteredPortfolio.length);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") setCurrentImage((prev) => (prev + 1) % filteredPortfolio.length);
      if (e.key === "ArrowLeft") setCurrentImage((prev) => (prev - 1 + filteredPortfolio.length) % filteredPortfolio.length);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, filteredPortfolio.length]);

  return (
    <main data-testid="portfolio-page" className="pt-24">
      {/* Hero Section */}
      <section data-testid="portfolio-hero" className="section pb-12">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-gold text-sm uppercase tracking-[0.3em] mb-4 block">
              Our Work
            </span>
            <h1 className="font-serif text-5xl md:text-7xl font-medium text-white mb-6">
              Portfolio
            </h1>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              A curated collection of our finest work, showcasing the artistry and 
              emotion we bring to every project.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section data-testid="portfolio-filter" className="pb-12">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                data-testid={`filter-${category.id}`}
                onClick={() => setActiveCategory(category.id)}
                className={`px-6 py-3 text-sm uppercase tracking-widest transition-all duration-300 ${
                  activeCategory === category.id
                    ? "bg-gold text-black font-bold"
                    : "bg-transparent border border-white/20 text-white/70 hover:border-gold hover:text-gold"
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section data-testid="portfolio-grid-section" className="pb-24">
        <div className="container-custom">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, index) => (
                <div
                  key={index}
                  className="aspect-[3/4] bg-surface animate-pulse"
                />
              ))}
            </div>
          ) : (
            <motion.div
              layout
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              <AnimatePresence mode="popLayout">
                {filteredPortfolio.map((item, index) => (
                  <motion.div
                    key={item.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.4 }}
                    data-testid={`portfolio-item-${index}`}
                    className="portfolio-item group cursor-pointer aspect-[3/4] overflow-hidden relative"
                    onClick={() => openLightbox(index)}
                  >
                    <img
                      src={item.image_url}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-1200 ease-slow group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors duration-500" />
                    <div className="absolute inset-0 flex flex-col justify-end p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <span className="text-gold text-xs uppercase tracking-widest mb-2">
                        {item.category}
                      </span>
                      <h3 className="font-serif text-xl text-white">{item.title}</h3>
                      {item.description && (
                        <p className="text-white/70 text-sm mt-2">{item.description}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          )}

          {!loading && filteredPortfolio.length === 0 && (
            <div className="text-center py-20">
              <p className="text-white/60 text-lg">No items found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && filteredPortfolio.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="lightbox"
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={closeLightbox}
          >
            {/* Close Button */}
            <button
              data-testid="lightbox-close"
              onClick={closeLightbox}
              className="absolute top-6 right-6 text-white/70 hover:text-white z-10 transition-colors"
            >
              <X size={32} strokeWidth={1} />
            </button>

            {/* Navigation */}
            <button
              data-testid="lightbox-prev"
              onClick={(e) => {
                e.stopPropagation();
                prevImage();
              }}
              className="absolute left-6 text-white/70 hover:text-white z-10 transition-colors"
            >
              <ChevronLeft size={48} strokeWidth={1} />
            </button>
            <button
              data-testid="lightbox-next"
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
              className="absolute right-6 text-white/70 hover:text-white z-10 transition-colors"
            >
              <ChevronRight size={48} strokeWidth={1} />
            </button>

            {/* Image */}
            <motion.div
              key={currentImage}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="max-w-[90vw] max-h-[85vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={filteredPortfolio[currentImage]?.image_url}
                alt={filteredPortfolio[currentImage]?.title}
                className="max-w-full max-h-[85vh] object-contain"
              />
              <div className="text-center mt-6">
                <h3 className="font-serif text-xl text-white">
                  {filteredPortfolio[currentImage]?.title}
                </h3>
                <p className="text-white/50 text-sm mt-1">
                  {currentImage + 1} / {filteredPortfolio.length}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <WhatsAppButton />
    </main>
  );
}
